import React, { useState, useRef } from 'react';
import { Upload, FileText, X, Package, CheckCircle, AlertTriangle, Info } from 'lucide-react';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import api from '../services/api';
import { API_ROUTES } from '../config/api';
import { isValidFileType, getFileInputAccept, getFileTypeInfo } from '../utils/fileUpload';

interface StockDataUploadProps {
    databaseId?: number | string | null;
    onUploadSuccess?: () => void;
}

interface UploadResult {
    message: string;
    created: number;
    updated: number;
    total_rows: number;
    errors_count: number;
    errors?: string[];
    additional_errors?: number;
}

interface UploadResultModalProps {
    isOpen: boolean;
    onClose: () => void;
    result: UploadResult;
}

const UploadResultModal: React.FC<UploadResultModalProps> = ({ isOpen, onClose, result }) => {
    if (!isOpen) return null;

    const hasErrors = result.errors_count > 0;
    const hasWarnings = result.errors && result.errors.length > 0;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-2xl mx-4 max-h-[80vh] overflow-y-auto w-full">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${hasErrors ? 'bg-amber-100' : 'bg-green-100'}`}>
                            {hasErrors ? (
                                <AlertTriangle className={`${hasErrors ? 'text-amber-600' : 'text-green-600'}`} size={20} />
                            ) : (
                                <CheckCircle className="text-green-600" size={20} />
                            )}
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">
                            Résultat de l'import
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                    >
                        <X size={20} />
                    </button>
                </div>
                
                <div className="space-y-4">
                    {/* Summary Statistics */}
                    <div className={`p-4 rounded-lg border ${hasErrors ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                        <h4 className={`font-medium mb-3 ${hasErrors ? 'text-amber-900' : 'text-green-900'}`}>
                            {hasErrors ? 'Import terminé avec des erreurs' : 'Import terminé avec succès'}
                        </h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className={hasErrors ? 'text-amber-800' : 'text-green-800'}>
                                <span className="font-medium">Entrées créées:</span> {result.created}
                            </div>
                            <div className={hasErrors ? 'text-amber-800' : 'text-green-800'}>
                                <span className="font-medium">Entrées mises à jour:</span> {result.updated}
                            </div>
                            <div className={hasErrors ? 'text-amber-800' : 'text-green-800'}>
                                <span className="font-medium">Total lignes:</span> {result.total_rows}
                            </div>
                            <div className={hasErrors ? 'text-amber-800' : 'text-green-800'}>
                                <span className="font-medium">Erreurs:</span> {result.errors_count}
                            </div>
                        </div>
                        
                        {result.created + result.updated > 0 && (
                            <div className="mt-3 p-2 bg-white bg-opacity-50 rounded border text-xs">
                                <strong>Succès:</strong> {result.created + result.updated} entrée(s) traitée(s) avec succès
                            </div>
                        )}
                    </div>

                    {/* Error Details */}
                    {hasWarnings && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                            <h4 className="font-medium text-red-900 mb-3 flex items-center gap-2">
                                <AlertTriangle size={16} />
                                Détails des erreurs
                            </h4>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                {result.errors!.map((error, index) => (
                                    <div key={index} className="text-sm text-red-800 p-2 bg-white bg-opacity-50 rounded border">
                                        {error}
                                    </div>
                                ))}
                            </div>
                            
                            {result.additional_errors && result.additional_errors > 0 && (
                                <div className="mt-3 p-2 bg-red-100 border border-red-300 rounded text-xs text-red-700">
                                    <strong>Note:</strong> {result.additional_errors} erreur(s) supplémentaire(s) non affichée(s). 
                                    Consultez les logs du serveur pour la liste complète.
                                </div>
                            )}
                        </div>
                    )}

                    {/* Success Message */}
                    {!hasErrors && (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-blue-800 text-sm">
                                🎉 Toutes les données ont été importées avec succès ! Vous pouvez maintenant utiliser 
                                ces données de stock dans vos analyses et prédictions.
                            </p>
                        </div>
                    )}
                </div>
                
                <div className="flex justify-end mt-6">
                    <button
                        onClick={onClose}
                        className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                            hasErrors 
                                ? 'bg-amber-600 text-white hover:bg-amber-700' 
                                : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                    >
                        Fermer
                    </button>
                </div>
            </div>
        </div>
    );
};

interface FormatModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const FormatModal: React.FC<FormatModalProps> = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Format Fichier Stock</h3>
                    <button
                        onClick={onClose}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                    >
                        <X size={20} />
                    </button>
                </div>
                
                <div className="space-y-4">
                    <div>
                        <h4 className="font-medium text-gray-900 mb-2">Colonnes requises</h4>
                        <ul className="text-sm text-gray-700 space-y-1">
                            <li><strong>article_id</strong> (obligatoire): Identifiant article, doit exister dans la base (colonne <code className="font-mono">ref</code> des articles).</li>
                            <li><strong>date</strong> (obligatoire): Date au format <code className="font-mono">YYYY-MM-DD</code>.</li>
                            <li><strong>quantity</strong> (obligatoire): Quantité (nombre; valeurs non numériques sont rejetées).</li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-medium text-gray-900 mb-2">Règles d'import</h4>
                        <ul className="text-sm text-gray-700 space-y-1">
                            <li><strong>Formats:</strong> {getFileTypeInfo().formats}</li>
                            <li><strong>Encodages CSV:</strong> utf-8, latin1, cp1252 (détection automatique)</li>
                            <li><strong>Délimiteurs CSV:</strong> ; , tabulation, | (détection automatique)</li>
                            <li><strong>Excel multi-feuilles:</strong> seule la première feuille est lue</li>
                            <li><strong>Taille conseillée:</strong> ≤ 50 Mo</li>
                            <li>Si une ligne référence un article inconnu, elle est ignorée et listée en erreur.</li>
                            <li>Pour un couple (article_id, date) existant, la quantité est <strong>mise à jour</strong> (remplacement).</li>
                            <li>Le fichier peut contenir plusieurs dates et articles; le traitement est en lot (créations/mises à jour).</li>
                        </ul>
                    </div>
                    
                    <div>
                        <h4 className="font-medium text-gray-900 mb-2">Exemple:</h4>
                        <div className="p-3 bg-gray-50 rounded border text-xs font-mono">
                            article_id,date,quantity<br/>
                            12345,2024-01-15,150<br/>
                            12345,2024-01-16,148<br/>
                            67890,2024-01-15,75
                        </div>
                    </div>
                    
                    <div className="p-3 bg-orange-50 border border-orange-200 rounded">
                        <p className="text-sm text-orange-800">
                            <strong>Note:</strong> Les colonnes doivent être exactement <code className="font-mono">article_id,date,quantity</code>. Les autres colonnes sont ignorées.
                        </p>
                    </div>
                </div>
                
                <div className="flex justify-end mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                    >
                        Compris
                    </button>
                </div>
            </div>
        </div>
    );
};

const StockDataUpload: React.FC<StockDataUploadProps> = ({ databaseId, onUploadSuccess }) => {
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [dragActive, setDragActive] = useState<boolean>(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const [showFormatModal, setShowFormatModal] = useState<boolean>(false);
    const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
    const [showResultModal, setShowResultModal] = useState<boolean>(false);
    const { activeDatabase } = useDatabaseContext();
    const inputRef = useRef<HTMLInputElement>(null);

    // Find the target database ID - prioritize databaseId prop over activeDatabase
    const targetDatabaseId = databaseId || activeDatabase?.id || null;

    // Use shared file type validation

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const selectedFile = event.target.files[0];
            if (isValidFileType(selectedFile)) {
                setFile(selectedFile);
                setError('');
                setUploadResult(null);
                setShowResultModal(false);
            } else {
                setError(getFileTypeInfo().errorMessage);
            }
        }
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const droppedFile = e.dataTransfer.files[0];
            if (isValidFileType(droppedFile)) {
                setFile(droppedFile);
                setError('');
                setUploadResult(null);
                setShowResultModal(false);
            } else {
                setError(getFileTypeInfo().errorMessage);
            }
        }
    };

    const handleClick = () => {
        inputRef.current?.click();
    };

    const removeFile = () => {
        setFile(null);
        if (inputRef.current) {
            inputRef.current.value = '';
        }
        setError('');
        setUploadResult(null);
        setShowResultModal(false);
    };

    const handleSubmitWithConfirmation = () => {
        if (!file || !targetDatabaseId) {
            setError('Veuillez sélectionner un fichier');
            return;
        }
        setShowConfirmDialog(true);
    };

    const handleCloseResultModal = () => {
        setShowResultModal(false);
        setUploadResult(null);
    };

    const handleConfirmedSubmit = async () => {
        if (!file || !targetDatabaseId) return;

        const formData = new FormData();
        formData.append('file', file);

        setLoading(true);
        setError('');
        setShowConfirmDialog(false);
        setUploadResult(null);
        setShowResultModal(false);

        try {
            const uploadUrl = API_ROUTES.STOCK.BASE + API_ROUTES.STOCK.UPLOAD(String(targetDatabaseId));
            console.debug('[StockDataUpload] Uploading stock file...', {
                uploadUrl,
                databaseId: targetDatabaseId,
                fileName: file.name,
                fileSize: file.size
            });
            const response = await api.post(uploadUrl, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            setUploadResult(response.data);
            setShowResultModal(true);
            setFile(null);
            
            // Call success callback if provided
            onUploadSuccess?.();
            
            if (inputRef.current) {
                inputRef.current.value = '';
            }
            
        } catch (err: any) {
            setError(err.response?.data?.error || 'Une erreur est survenue lors de l\'import');
        } finally {
            setLoading(false);
        }
    };

    if (!targetDatabaseId) {
        return (
            <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-xl">
                <p className="text-yellow-800 text-sm">
                    Aucune base de données sélectionnée. Veuillez sélectionner une base de données avant d'importer des données de stock.
                </p>
            </div>
        );
    }

    return (
        <div className="max-w-4xl space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-orange-100 rounded-xl">
                        <Package className="text-orange-600" size={24} />
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                            Données de Stock
                        </h2>
                        <p className="text-gray-600 text-sm">
                            Importez les niveaux de stock pour {activeDatabase?.name || 'votre base'}
                        </p>
                    </div>
                </div>
                
                <button
                    onClick={() => setShowFormatModal(true)}
                    className="flex items-center gap-2 px-3 py-2 text-orange-600 hover:text-orange-800 hover:bg-orange-50 rounded-lg transition-colors"
                >
                    <Info size={16} />
                    Format Fichier
                </button>
            </div>

            {/* Drag & Drop Area - Compact */}
            <div
                className={`relative border-2 border-dashed rounded-xl p-4 transition-all cursor-pointer group ${
                    dragActive 
                        ? 'border-orange-400 bg-orange-50' 
                        : file 
                            ? 'border-green-400 bg-green-50' 
                            : 'border-gray-300 hover:border-orange-300 hover:bg-orange-50/30'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={handleClick}
            >
                <input
                    ref={inputRef}
                    type="file"
                    onChange={handleFileChange}
                    accept={getFileInputAccept()}
                    className="hidden"
                    disabled={loading}
                />
                
                {file ? (
                    // File Selected State - Horizontal Layout
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                                <FileText className="text-green-600" size={20} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold text-gray-900 text-sm truncate">{file.name}</span>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeFile();
                                        }}
                                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors flex-shrink-0"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                                <p className="text-gray-600 text-xs">
                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                            </div>
                        </div>
                        
                        {/* Import Button */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSubmitWithConfirmation();
                            }}
                            disabled={loading}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium text-sm flex-shrink-0"
                        >
                            {loading ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <Upload size={16} />
                            )}
                            {loading ? 'Import...' : 'Importer'}
                        </button>
                    </div>
                ) : (
                    // Drop Zone State - Horizontal Layout
                    <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-lg transition-all flex-shrink-0 ${
                            dragActive ? 'bg-orange-200' : 'bg-gray-100 group-hover:bg-orange-100'
                        }`}>
                            <Package className={`transition-colors ${
                                dragActive ? 'text-orange-600' : 'text-gray-400 group-hover:text-orange-500'
                            }`} size={24} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-semibold text-gray-900 mb-1">
                                {dragActive ? 'Déposez votre fichier ici' : 'Glissez vos données de stock'}
                            </h3>
                            <p className="text-xs text-gray-600">
                                ou <span className="text-orange-600 font-medium">cliquez pour parcourir</span>
                                {' • '}
                                <span className="text-gray-500">CSV, XLSX, XLS</span>
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Confirmation Dialog */}
            {showConfirmDialog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 max-w-md mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-orange-100 rounded-lg">
                                <AlertTriangle className="text-orange-600" size={20} />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                Confirmer l'import
                            </h3>
                        </div>
                        <p className="text-gray-600 mb-6">
                            Êtes-vous sûr de vouloir importer ces données de stock ? 
                            Les données existantes pour les mêmes articles et dates seront remplacées.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShowConfirmDialog(false)}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleConfirmedSubmit}
                                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                            >
                                Confirmer l'import
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Format Modal */}
            <FormatModal isOpen={showFormatModal} onClose={() => setShowFormatModal(false)} />

            {/* Upload Result Modal */}
            {uploadResult && (
                <UploadResultModal 
                    isOpen={showResultModal}
                    onClose={handleCloseResultModal}
                    result={uploadResult}
                />
            )}

            {/* Error Messages */}
            {error && (
                <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm flex items-start gap-3">
                    <X className="text-red-500 mt-0.5 flex-shrink-0" size={16} />
                    {error}
                </div>
            )}
        </div>
    );
};

export default StockDataUpload; 